import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta';
import { fetchLocalizedCollection } from '../../lib/cms';

export default function JobsPageEn() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'en').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find(
        (s) => getSlugVal(s) === 'pmb-job-ledige-stillinger' || getSlugVal(s) === 'jobs'
      );
      if (match) setPageData(match);
    });
  }, []);

  const title = pageData?.titel || 'Job openings';
  const badge = pageData?.undertitel || 'Careers at PMB';
  const intro =
    pageData?.intro ||
    'At PMB, we engineer machinery that demands both millimeter precision and deep technical care – and the same dedication applies to the people who build, assemble, and maintain them.';
  const seoTitle =
    pageData?.seo_titel || 'PMB Careers – Job Openings & Opportunities';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'Careers at POVL MØLLERS MASKINFABRIK A/S in Borup, Denmark. Become part of a strong team with over 100 years of precision engineering experience.';

  return (
    <>
      <SeoMeta
        title={seoTitle}
        description={metaDesc}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">About us</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Job openings</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              {badge}
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              {intro}
            </p>
            <p className="mt-2 text-sm sm:text-base text-stone-400 leading-relaxed">
              Here you have the opportunity to apply your technical skills in a company with more than a century of experience – where genuine quality, safety, and mutual respect remain cornerstone values.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* General Intro Box */}
          <div className="p-6 sm:p-8 rounded-lg bg-stone-50 border border-stone-200 text-stone-800 text-base leading-relaxed space-y-3">
            <p>
              We look for colleagues who enjoy taking ownership, executing high-quality craftmanship, and contributing to a supportive everyday team atmosphere.
            </p>
            <p>
              Whether you are an installation technician, mechanical engineer, smith, or bring background from manufacturing and field service, there could be a place for you with us.
            </p>
          </div>

          {/* Job Card: Freelance installer for central lubrication */}
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden shadow-sm hover:border-[#006368] transition">
            <div className="bg-[#006368] text-white px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-white/80">
                  Open Position
                </span>
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                  Freelance Installer for Central Lubrication
                </h2>
              </div>
              <span className="text-xs bg-white/15 px-3 py-1.5 rounded font-medium whitespace-nowrap self-start sm:self-auto">
                Posted: Active position
              </span>
            </div>

            <div className="p-6 sm:p-8 space-y-6 text-stone-800 text-sm sm:text-base leading-relaxed">
              <h3 className="text-lg font-bold text-[#006368] italic">
                Do you thrive on independent mechanical assembly work, service visits, and varied working days with industrial clients?
              </h3>

              <p>
                At PMB – Povl Møllers Maskinfabrik A/S, we offer a versatile daily work life where you work closely with clients, engineers, and technicians in a role with high autonomy, responsibility, and diversity.
              </p>

              <p>
                We are looking for an experienced freelance installer for assembly and service of centralized lubrication systems, working on independent field installations at our industrial customer premises. You become part of an established company where technical competence, flexibility, and lasting partnerships guide everything we do.
              </p>

              <div className="pt-4 border-t border-stone-100">
                <h4 className="font-bold text-stone-900 text-base mb-3">
                  Your primary duties include installation and servicing of central lubrication systems across Jutland and Funen:
                </h4>
                <ul className="list-disc pl-5 space-y-2 text-stone-700 text-sm">
                  <li>Mounting and servicing different types of centralized lubrication systems</li>
                  <li>Working with market-leading solutions from Vogel, SKF, and Lincoln</li>
                  <li>Planning and executing assembly tasks autonomously on-site at client premises</li>
                  <li>Diagnosing faults and tailoring pipework and divider routing in dialogue with client operators</li>
                  <li>Upholding quality standards and maintaining professional customer relations</li>
                  <li>Collaborating with our technical team in Borup to deliver exceptional customer satisfaction</li>
                </ul>
              </div>

              <div className="pt-4 border-t border-stone-100">
                <h4 className="font-bold text-stone-900 text-base mb-3">
                  You are a strong fit if:
                </h4>
                <ul className="list-disc pl-5 space-y-2 text-stone-700 text-sm">
                  <li>You have hands-on experience with mechanical assembly, hydraulics, machine repair, or heavy equipment</li>
                  <li>You are organized, self-motivated, and take pride in neat pipe routing and dependable work</li>
                  <li>You hold a valid driver's license (Category B)</li>
                  <li>You are proactive, service-minded, and communicate clearly in Danish or English</li>
                </ul>
              </div>

              <div className="pt-6 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-stone-50 p-6 rounded-lg">
                <div>
                  <h5 className="font-bold text-stone-900 text-base">
                    Interested in this position?
                  </h5>
                  <p className="text-xs text-stone-600 mt-1">
                    Send your CV and a brief introduction to{' '}
                    <a
                      href="mailto:info@pmborup.dk"
                      className="text-[#006368] font-bold hover:underline"
                    >
                      info@pmborup.dk
                    </a>{' '}
                    or call us at{' '}
                    <a
                      href="tel:+4557526262"
                      className="text-[#006368] font-bold hover:underline whitespace-nowrap"
                    >
                      +45 57 52 62 62
                    </a>
                    .
                  </p>
                </div>
                <Link
                  to="/en/contact"
                  className="px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm transition whitespace-nowrap text-center"
                >
                  Contact PMB
                </Link>
              </div>
            </div>
          </div>

          {/* Unsolicited Applications Box */}
          <div className="p-8 rounded-xl bg-stone-900 text-white text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="space-y-2">
              <h3 className="text-xl sm:text-2xl font-bold">
                Unsolicited Applications
              </h3>
              <p className="text-sm text-stone-400 max-w-xl">
                We are always pleased to hear from qualified technicians, machine builders, and service personnel. Send us your profile and let us explore future opportunities.
              </p>
            </div>
            <a
              href="mailto:info@pmborup.dk"
              className="px-6 py-3 rounded bg-white text-stone-900 font-semibold text-sm hover:bg-stone-100 transition whitespace-nowrap"
            >
              Send Unsolicited Application
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
